import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Typography,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ExpandLess,
  ExpandMore,
  Gavel,
  Add,
  List as ListIcon,
  Settings,
  Description,
  People,
  Person,
  AccountTree,
  Category,
  Schedule,
  Extension,
  Mail as MailIcon,
  SendAndArchive,
  Inbox, 
  ViewList,
  Newspaper
} from "@mui/icons-material";
import { jwtDecode } from 'jwt-decode';
import { getModulosPorUsuario } from "../http/actions/ModuloAction";

// Estilos personalizados (sin cambios)
const StyledListItem = styled(ListItem)(({ theme }) => ({
  "& .MuiListItemText-primary": {
    color: "inherit",
    fontSize: "0.8rem",
  },
  "& .MuiListItemIcon-root": {
    minWidth: "35px",
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledLink = styled(Link)({
  textDecoration: "none",
  color: "inherit",
  display: "flex",
  width: "100%",
});

const Sidebar = ({ mobileOpen, handleDrawerToggle, isOpen, isSmallScreen }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const [userRole, setUserRole] = useState('');
  const [serviciosCliente, setServiciosCliente] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.rol);

        if (decoded.rol === 'Usuario') {
          await Promise.all([
            fetchServiciosUsuario(decoded.oid)
          ]);
        }
      } catch (error) {
        console.error('Error initializing user data:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeUserData();
  }, []);

  const toggleExpand = (item) => {
    setExpandedItems((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  const fetchServiciosUsuario = async (idUsuario) => {
    const token = localStorage.getItem('token');
    try {
      const response = await getModulosPorUsuario(token, idUsuario);
      setServiciosCliente(response);
    } catch (error) {
      console.error('Error fetching servicios:', error);
    }
  };

  const tieneServicio = (nombreServicio) => {
    return serviciosCliente.some(servicio => 
      servicio.servicio.toLowerCase().includes(nombreServicio.toLowerCase())
    );
  };

  const drawer = (
    <>
      <Box sx={{ p: 2 }}>
        <StyledLink to="/home">
          <Typography variant="h5" component="div">
            Austranet
          </Typography>
        </StyledLink>
      </Box>
      <List>
        {userRole === 'Administrador' ? (
          <>
            {/* Sección Normativas */}
            <StyledListItem onClick={() => toggleExpand("normativas")}>
              <ListItemIcon>
                <Gavel />
              </ListItemIcon>
              <ListItemText primary="Normativas" />
              {expandedItems.normativas ? <ExpandLess /> : <ExpandMore />}
            </StyledListItem>
            <Collapse in={expandedItems.normativas} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/nueva-normativa">
                    <ListItemIcon>
                      <Add />
                    </ListItemIcon>
                    <ListItemText primary="Agregar normativa" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/gestor-normativa">
                    <ListItemIcon>
                      <ListIcon />
                    </ListItemIcon>
                    <ListItemText primary="Gestor de normativas" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/envio-correo-normativa">
                    <ListItemIcon>
                      <MailIcon />
                    </ListItemIcon>
                    <ListItemText primary="Envio de correos manual" />
                  </StyledLink>
                </StyledListItem>

                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/newsletter">
                    <ListItemIcon>
                      <Newspaper />
                    </ListItemIcon>
                    <ListItemText primary="Newsletter" />
                  </StyledLink>
                </StyledListItem>
              </List>
            </Collapse>

            {/* Bandeja Normativa */}
            <StyledListItem onClick={() => toggleExpand("bandejaNormativa")}>
              <ListItemIcon>
                <Inbox />
              </ListItemIcon>
              <ListItemText primary="Scraper normativas" />
              {expandedItems.bandejaNormativa ? <ExpandLess /> : <ExpandMore />}
            </StyledListItem>
            <Collapse in={expandedItems.bandejaNormativa} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/bandeja-normativas">
                    <ListItemIcon>
                      <ViewList />
                    </ListItemIcon>
                    <ListItemText primary="Bandeja de Normativas" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/horario-envio-scraper">
                    <ListItemIcon>
                      <ViewList />
                    </ListItemIcon>
                    <ListItemText primary="Horario Envío Scraper" />
                  </StyledLink>
                </StyledListItem>
              </List>
            </Collapse>

            {/* Sección Administración General */}
            <StyledListItem onClick={() => toggleExpand("admin")}>
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText primary="Administración General" />
              {expandedItems.admin ? <ExpandLess /> : <ExpandMore />}
            </StyledListItem>
            <Collapse in={expandedItems.admin} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <StyledListItem sx={{ pl: 4 }} onClick={() => toggleExpand("clientes")}>
                  <ListItemIcon>
                    <People />
                  </ListItemIcon>
                  <ListItemText primary="Clientes" />
                  {expandedItems.clientes ? <ExpandLess /> : <ExpandMore />}
                </StyledListItem>
                <Collapse in={expandedItems.clientes} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <StyledListItem sx={{ pl: 6 }}>
                      <StyledLink to="/cliente/correos">
                        <ListItemIcon>
                          <Description />
                        </ListItemIcon>
                        <ListItemText primary="Correos" />
                      </StyledLink>
                    </StyledListItem>
                    <StyledListItem sx={{ pl: 6 }}>
                      <StyledLink to="/cliente/configuracion-correo">
                        <ListItemIcon>
                          <Description />
                        </ListItemIcon>
                        <ListItemText primary="Configurar envio correos " />
                      </StyledLink>
                    </StyledListItem>
                    <StyledListItem sx={{ pl: 6 }}>
                      <StyledLink to="/cliente/gestor-clientes">
                        <ListItemIcon>
                          <Person />
                        </ListItemIcon>
                        <ListItemText primary="Gestor de Clientes" />
                      </StyledLink>
                    </StyledListItem>
                    <StyledListItem sx={{ pl: 6 }}>
                      <StyledLink to="/cliente/gestor-organismos">
                        <ListItemIcon>
                          <AccountTree />
                        </ListItemIcon>
                        <ListItemText primary="Gestionar Organismos" />
                      </StyledLink>
                    </StyledListItem>
                    <StyledListItem sx={{ pl: 6 }}>
                      <StyledLink to="/cliente/gestor-rubros">
                        <ListItemIcon>
                          <Category />
                        </ListItemIcon>
                        <ListItemText primary="Gestionar Rubros" />
                      </StyledLink>
                    </StyledListItem>
                    <StyledListItem sx={{ pl: 6 }}>
                      <StyledLink to="/cliente/gestor-modulos">
                        <ListItemIcon>
                          <Extension />
                        </ListItemIcon>
                        <ListItemText primary="Módulos" />
                      </StyledLink>
                    </StyledListItem>
                    <StyledListItem sx={{ pl: 6 }}>
                      <StyledLink to="/cliente/horario-clientes">
                        <ListItemIcon>
                          <Schedule />
                        </ListItemIcon>
                        <ListItemText primary="Horario Clientes" />
                      </StyledLink>
                    </StyledListItem>
                  </List>
                </Collapse>

                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/clasificacion-documento">
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary="Clasificación Documento" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/enlace-congreso">
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary="Enlace de congreso" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/estado-tramitacion">
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary="Estado de tramitación" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/organismo-emisor">
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary="Organismo Emisor" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/rubros">
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary="Rubros" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/tipo-documento">
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary="Tipo de Documento" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/paises">
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary="Paises" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/usuarios">
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary="Usuarios" />
                  </StyledLink>
                </StyledListItem>
                <StyledListItem sx={{ pl: 4 }}>
                  <StyledLink to="/logs">
                    <ListItemIcon>
                      <Description />
                    </ListItemIcon>
                    <ListItemText primary="Gestor de logs" />
                  </StyledLink>
                </StyledListItem>
              </List>
            </Collapse>
          </>
        ) : (
          <>
            <StyledListItem>
              <StyledLink to="/usuario/gestor-normativa">
                <ListItemIcon>
                  <Gavel />
                </ListItemIcon>
                <ListItemText primary="Normativas históricas" />
              </StyledLink>
            </StyledListItem>
          </>
        )}

        {/* Mostrar servicios dinámicamente para usuarios */}
        {userRole === 'Usuario' && !loading && (
          <>
            {tieneServicio("Integracion AustranetGRC") && (
              <StyledListItem>
                <StyledLink to="/usuario/integracion-grc-normativas">
                  <ListItemIcon>
                    <SendAndArchive />
                  </ListItemIcon>
                  <ListItemText primary="Enviar a AustranetGRC" />
                </StyledLink>
              </StyledListItem>
            )}
          </>
        )}
      </List>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{ 
        width: { 
          xs: 0, 
          sm: isOpen && !isSmallScreen ? 240 : 0 
        }, 
        flexShrink: { sm: 0 } 
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 240,
            bgcolor: "#f8f9fa",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="persistent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            bgcolor: "#f8f9fa",
            transition: "width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
            width: isOpen && !isSmallScreen ? 240 : 0,
            overflowX: "hidden",
          },
        }}
        open={isOpen && !isSmallScreen}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;